import React, { useEffect, useState } from "react";
import { History } from "lucide-react";
import SearchableSelect from "modules/swiftboard/components/ui/SearchableSelect";
import DateTimePicker from "modules/swiftboard/components/ui/DateTimePicker";
import apiShared from "modules/swiftboard/api";
import axiosClient from "api/axios_client.js";
import { ROLE_OPTIONS, LOGIN_FILTER_OPTIONS } from "../../features/campaign.constants";

const StepAudience = ({ audienceFilter, setFilterField }) => {
  const [companyOptions, setCompanyOptions] = useState([]);
  const [tradeOptions, setTradeOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const results = await Promise.allSettled([
        apiShared.getAllCompanies({ limit: 500, skip: 0 }),
        apiShared.getAllTradeOfWork({ limit: 500, skip: 0 }),
        axiosClient.post("/user/get-all", { limit: 500, skip: 0, is_activated: true }),
      ]);
      if (cancelled) return;

      const [companiesRes, tradesRes, usersRes] = results;

      setCompanyOptions(
        companiesRes.status === "fulfilled"
          ? (companiesRes.value.data?.data || []).map((c) => ({ value: c.id, label: c.company_name }))
          : []
      );
      setTradeOptions(
        tradesRes.status === "fulfilled"
          ? (tradesRes.value.data?.data || []).map((t) => ({ value: t.id, label: t.trade_name }))
          : []
      );
      setUserOptions(
        usersRes.status === "fulfilled"
          ? (usersRes.value.data?.data || []).map((u) => ({
              value: u.id,
              label: `${u.first_name || ""} ${u.last_name || ""}`.trim() + ` (${u.email})`,
            }))
          : []
      );
      setLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="grid grid-cols-1 gap-4">
      <p className="text-xs text-text_secondary">
        Combine filters to build your audience — selections within a filter are OR'd together, filters are AND'd together.
      </p>

      <div>
        <label className="block text-xs font-medium text-text_secondary mb-1.5">By Role</label>
        <SearchableSelect
          isMulti
          options={ROLE_OPTIONS}
          value={audienceFilter.roles}
          onChange={(val) => setFilterField("roles", val)}
          placeholder="Select roles (Client / Admin / Employee)"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-text_secondary mb-1.5">By Company</label>
        <SearchableSelect
          isMulti
          options={companyOptions}
          value={audienceFilter.company_fids}
          onChange={(val) => setFilterField("company_fids", val)}
          placeholder="Select companies"
          isLoading={loading}
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-text_secondary mb-1.5">By Trade of Work</label>
        <SearchableSelect
          isMulti
          options={tradeOptions}
          value={audienceFilter.trade_of_work_fids}
          onChange={(val) => setFilterField("trade_of_work_fids", val)}
          placeholder="Select trades of work"
          isLoading={loading}
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-text_secondary mb-1.5">By Specific User</label>
        <SearchableSelect
          isMulti
          options={userOptions}
          value={audienceFilter.user_fids}
          onChange={(val) => setFilterField("user_fids", val)}
          placeholder="Search and select individual users"
          isLoading={loading}
        />
      </div>

      <div className="rounded-lg border border-border p-3 space-y-3">
        <div className="flex items-center gap-1.5 text-xs font-medium text-text_secondary">
          <History size={13} className="text-text_muted" />
          By Login Activity
        </div>

        <div className="flex flex-wrap gap-1.5">
          {LOGIN_FILTER_OPTIONS.map((opt) => {
            const selected = audienceFilter.login_filter === opt.value;
            return (
              <button
                type="button"
                key={opt.value}
                onClick={() => setFilterField("login_filter", opt.value)}
                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium border ${
                  selected ? "border-primary bg-primary_tint text-primary" : "border-border bg-surface text-text_secondary"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {audienceFilter.login_filter === "CUSTOM_RANGE" && (
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div>
              <label className="block text-[11px] text-text_muted mb-1">From</label>
              <DateTimePicker
                value={audienceFilter.login_from}
                onChange={(d) => setFilterField("login_from", d)}
                maxDate={audienceFilter.login_to || new Date()}
                showTime={false}
                placeholder="Any date"
              />
            </div>
            <div>
              <label className="block text-[11px] text-text_muted mb-1">To</label>
              <DateTimePicker
                value={audienceFilter.login_to}
                onChange={(d) => setFilterField("login_to", d)}
                minDate={audienceFilter.login_from}
                maxDate={new Date()}
                showTime={false}
                placeholder="Any date"
              />
            </div>
            {!audienceFilter.login_from && !audienceFilter.login_to && (
              <p className="text-[11px] text-text_muted col-span-2">Pick at least one date to continue.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StepAudience;
